// Rich-text HTML sanitizálás — a tárolt rich-text (üzenet-törzs / ticket-komment / leírás) megjelenítésének
// KÖZPONTI biztonsági pontja (stored-XSS elleni fal). MINDEN render-út (élő szerkesztés kimenete + migrált
// legacy HTML) ezen megy át, és a `dangerouslySetInnerHTML` KIZÁRÓLAG ennek a kimenetét kapja.
//
// AZ ALLOWLIST NEM ITT ÉL: a tag/attribútum/CSS-készlet a `@atlas/shared` `rich-text-policy`-jában van, mert
// ugyanazt a policy-t a BFF szanitálója is használja. Előzmény: három egymástól független lista
// volt, és MÁR elcsúsztak — ebből a webes hiányzó `u`/`s`/`h2`/`blockquote`/`code`/`pre` azt jelentette, hogy a
// szerkesztőben létrehozott áthúzás/idézet/kód NÉMÁN eltűnt a megjelenítéskor.
//
// A linkeknél a `href` protokoll-allowlist blokkolja a `javascript:`/`data:` vektort, és minden anchor
// kényszerítetten `rel="noopener noreferrer" target="_blank"` lesz. A `style` attribútum PROPERTY-SZINTEN
// szűrt: a DOMPurify a CSS-t elemzi, de a tulajdonság-allowlistet mi kényszerítjük ki (lásd `filterStyle`).
import {
  EDITOR_IMAGE_SRC_RE,
  RICH_TEXT_ATTRS,
  RICH_TEXT_STYLE_PROPS,
  RICH_TEXT_TAGS,
} from '@atlas/shared';
import DOMPurify from 'dompurify';

const ALLOWED_TAGS = [...RICH_TEXT_TAGS];
/** A DOMPurify attribútum-listája LAPOS (nincs per-tag bontás) — a per-tag szigorítás a hookban történik. */
const ALLOWED_ATTR = [...new Set(Object.values(RICH_TEXT_ATTRS).flat())];
// href protokoll-allowlist: http(s)/mailto/tel + relatív/anchor. NINCS `javascript:`/`data:` (XSS-vektor).
const ALLOWED_URI_REGEXP = /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i;

/**
 * A `style` attribútum újraépítése a policy szerint: CSAK a felsorolt tulajdonságok, CSAK a felsorolt
 * érték-alakokkal. Üres eredmény → az attribútum törlődik (nem marad `style=""`).
 *
 * MIÉRT KELL a DOMPurify CSS-elemzése MELLETT: a DOMPurify a `style`-t biztonságosra tisztítja (nincs
 * `expression()`, `javascript:`), de MINDEN érvényes tulajdonságot átenged — pl. `position`/`width`/
 * `background-image` is átjutna, ami elrontja a lista/kártya-elrendezést, és `url()`-lel külső kérést indít.
 */
function filterStyle(node: Element): void {
  const raw = node.getAttribute('style');
  if (raw === null) return;
  const kept: string[] = [];
  for (const decl of raw.split(';')) {
    const idx = decl.indexOf(':');
    if (idx === -1) continue;
    const prop = decl.slice(0, idx).trim().toLowerCase();
    const value = decl.slice(idx + 1).trim();
    const patterns = RICH_TEXT_STYLE_PROPS[prop];
    if (!patterns) continue;
    if (patterns.some((re) => re.test(value))) kept.push(`${prop}: ${value}`);
  }
  if (kept.length === 0) node.removeAttribute('style');
  else node.setAttribute('style', kept.join('; '));
}

// A hookot EGYSZER regisztráljuk (modul-szint). A DOMPurify a projektben csak itt fut, így a globális hook
// biztonságos: minden anchor kap `rel`/`target`-et (tabnabbing + referrer-szivárgás ellen).
let hookRegistered = false;
function ensureHook(): void {
  if (hookRegistered) return;
  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (!(node instanceof Element)) return;
    const tag = node.tagName.toLowerCase();
    // PER-TAG szigorítás: a lapos DOMPurify-lista miatt egy `href` egy `<p>`-n is átjutna — itt esik ki.
    const allowedForTag = RICH_TEXT_ATTRS[tag] ?? [];
    for (const attr of [...node.attributes]) {
      if (!allowedForTag.includes(attr.name)) node.removeAttribute(attr.name);
    }
    if (tag === 'a') {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
    if (tag === 'video') {
      // UGYANAZ A FAL, mint az `img`-nél: csak a SAJÁT, stabil utunk marad — külső videó-URL, `blob:` és
      // `data:` forrás kiesik (a csomóponttal együtt, mert egy `src` nélküli `<video>` üres fekete téglalap).
      if (!EDITOR_IMAGE_SRC_RE.test(node.getAttribute('src') ?? '')) {
        node.remove();
        return;
      }
      // A VEZÉRLŐK KIKÉNYSZERÍTVE: `controls` nélkül a videó nem indítható el, és a tárolt HTML-ben (vagy a
      // migrált tartalomban) nem bízunk abban, hogy ott volt. A `preload="metadata"` a sávszélességet védi.
      node.setAttribute('controls', '');
      node.setAttribute('preload', 'metadata');
      return;
    }
    if (tag === 'img') {
      // A `getAttribute` a SANITIZÁLT értéket adja; ha nem a saját utunk, a csomópont MEGY (nem csak az
      // attribútum, mert egy `src` nélküli `<img>` alt-szöveges törött placeholder maradna).
      //
      // ⚠️ A MIGRÁLT LEGACY `<img src="/storage/wysiwyg-editor/…">` is ITT esik ki: a bájtok a régi szerver
      // fájlrendszerén élnek (a dumpban nincsenek) — a képek átköltöztetése cutover-tétel.
      if (!EDITOR_IMAGE_SRC_RE.test(node.getAttribute('src') ?? '')) node.remove();
      return;
    }
    filterStyle(node);
  });
  hookRegistered = true;
}

/** A tárolt/szerkesztett rich-text HTML sanitizálása a KÖZÖS policy szerint (a `dangerouslySetInnerHTML` bemenete). */
export function sanitizeRichTextHtml(html: string): string {
  ensureHook();
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOWED_URI_REGEXP,
  });
}

/** Rich-text HTML → tiszta szöveg (a látható karakterek). A leírás min-hossz-validáció a NYERS szöveg hosszára
 *  fut (nem a HTML-tag-ekre), így a formázás nem duzzasztja fel a karakterszámot. Sanitizálás után olvassa ki. */
export function richTextToPlainText(html: string): string {
  const clean = sanitizeRichTextHtml(html);
  const doc = new DOMParser().parseFromString(clean, 'text/html');
  // A block-elemek közti szöveget szóközzel választjuk el, majd a felesleges whitespace-t összevonjuk.
  const text = doc.body.textContent ?? '';
  return text.replace(/\s+/g, ' ').trim();
}

/**
 * ÜRES-e egy rich-text törzs? Nincs LÁTHATÓ szöveg ÉS nincs média (kép/videó) — a szanitálás UTÁN mérve, tehát
 * csak az a kép számít, ami a tárolt alakban is megmarad (a saját feltöltési utunk).
 *
 * MIÉRT NEM ELÉG a `richTextToPlainText(...) === ''`: a csak képből álló komment szövege üres, így a Küldés gomb
 * tiltva maradt (user-lelet: „a kommentben küldött kép a Küldés gombra sem megy el").
 */
export function isRichTextBlank(html: string): boolean {
  if (richTextToPlainText(html) !== '') return false;
  const doc = new DOMParser().parseFromString(sanitizeRichTextHtml(html), 'text/html');
  return doc.body.querySelector('img, video') === null;
}

/**
 * HTML-nek látszik-e a TÁROLT törzs? A MIGRÁLT (legacy) kommentek/jegyzetek SIMA SZÖVEGEK sortörésekkel —
 * azokat `white-space: pre-line`-nal kell rendereleni, különben a sortörések összeolvadnak. Az ÚJ tartalom (a
 * WYSIWYG-ből) HTML. Egy heurisztika dönt, mert a tárolt alak nem hordoz típus-jelzőt.
 *
 * KÖZÖS RÉTEG: a belső jegy-szál és a partner-portál szála UGYANEZT a döntést hozza — ha két helyen élne, az
 * egyik felület csendben összeolvasztaná a migrált szövegek sortöréseit.
 */
export function looksLikeStoredHtml(body: string): boolean {
  return /<(?:p|br|ul|ol|li|strong|em|u|s|mark|span|h2|h3|h4|blockquote|pre|code|a|img|video|hr)\b/i.test(
    body,
  );
}
